import { jsPDF } from 'jspdf';
import 'jspdf/dist/polyfills.es.js';
import { activityMatchLines } from './teamFixtures';

const NAVY   = [13,  34,  64];
const WHITE  = [255, 255, 255];
const GRAY   = [107, 114, 128];
const LGRAY  = [229, 231, 235];
const DKGRAY = [31,  41,  55];
const BLUE   = [26,  74, 138];

function setColor(doc, rgb) { doc.setTextColor(rgb[0], rgb[1], rgb[2]); }
function setFill(doc,  rgb) { doc.setFillColor(rgb[0], rgb[1], rgb[2]); }
function setDraw(doc,  rgb) { doc.setDrawColor(rgb[0], rgb[1], rgb[2]); }

function numDays(dep, ret) {
  if (!dep || !ret) return 1;
  const diff = Math.round((new Date(ret) - new Date(dep)) / 86400000);
  return Math.max(1, Math.min((diff || 0) + 1, 15));
}

function dayLabel(dep, idx) {
  if (!dep) return `Day ${idx + 1}`;
  const d = new Date(dep);
  d.setDate(d.getDate() + idx);
  return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

function fmtDate(str) {
  if (!str) return '';
  const [y, m, d] = str.split('-');
  return `${d}/${m}/${y}`;
}

function isTransferLike(type) {
  return ['transfer', 'transfers', 'travel'].includes((type || '').toLowerCase());
}

function displayType(type) {
  if (!type) return '';
  return isTransferLike(type) ? 'Travel' : type;
}

/**
 * Resolves the location blocks (name + address + Google Maps link) to show for
 * an itinerary item. The place's own address wins; supplier items fall back
 * to the supplier's address. Transfers show both pickup and drop-off.
 */
function getLocations(item) {
  const blocks = [];
  if (item.locationName) {
    blocks.push({
      name: item.locationName,
      address: item.locationAddress || item.supplierAddress || '',
      link: item.locationGoogleLink || item.supplierGoogleLink || '',
    });
  }
  if (isTransferLike(item.activityType)) {
    if (item.pickupLocation || item.pickupAddress) {
      blocks.push({
        name: `From: ${item.pickupLocation || item.pickupAddress}`,
        address: item.pickupLocation ? (item.pickupAddress || '') : '',
        link: item.pickupGoogleLink || '',
      });
    }
    if (item.dropoffLocation || item.dropoffAddress) {
      blocks.push({
        name: `To: ${item.dropoffLocation || item.dropoffAddress}`,
        address: item.dropoffLocation ? (item.dropoffAddress || '') : '',
        link: item.dropoffGoogleLink || '',
      });
    }
  }
  return blocks;
}

/**
 * Generates and triggers download of a simplified group itinerary PDF.
 *
 * @param {Array}       items        - All itinerary items (from Firestore)
 * @param {object}      request      - The request document
 * @param {string|null} crestDataUrl - Club crest as a data URL (PNG/JPEG), or null
 */
// `matches` ({ fixturesById, teamsById }) adds who-plays-who lines under a
// Fixtures activity — never the opposition/referee contacts.
export function generateGroupItineraryPdf(items, request, crestDataUrl = null, matches = null, { filterLabel = '' } = {}) {
  const doc    = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const PAGE_W = 210;
  const PAGE_H = 297;
  const ML     = 16;
  const MR     = 16;
  const CW     = PAGE_W - ML - MR;
  let y        = 0;

  const nDays  = numDays(request.departureDate, request.returnDate);
  const dep    = request.departureDate;
  const ret    = request.returnDate;
  const tourLabel = request.tourName || request.contactName || 'Tour Itinerary';
  const destination = request.destination || '';

  // ── Header band ────────────────────────────────────────────────────────────
  const HEADER_H = crestDataUrl ? 44 : 32;

  setFill(doc, NAVY);
  doc.rect(0, 0, PAGE_W, HEADER_H, 'F');

  // Crest
  if (crestDataUrl) {
    try {
      const crestSize = 30;
      const crestX    = PAGE_W - MR - crestSize;
      const crestY    = (HEADER_H - crestSize) / 2;
      doc.addImage(crestDataUrl, 'PNG', crestX, crestY, crestSize, crestSize, undefined, 'FAST');
    } catch (_) {}
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  setColor(doc, WHITE);
  doc.text(tourLabel, ML, 14);

  if (destination) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    setColor(doc, [160, 180, 220]);
    doc.text(destination, ML, 22);
  }

  if (dep || ret) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    setColor(doc, [160, 180, 220]);
    const dateStr = [dep && fmtDate(dep), ret && fmtDate(ret)].filter(Boolean).join(' – ');
    doc.text(dateStr, ML, destination ? 30 : 22);
  }

  y = HEADER_H + 10;

  // ── Subtitle ──────────────────────────────────────────────────────────────
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  setColor(doc, GRAY);
  doc.text(filterLabel
    ? `Filtered itinerary — ${filterLabel} activities only`
    : 'Full itinerary — all activities, times and locations', ML, y);
  y += 8;

  // ── Day sections ──────────────────────────────────────────────────────────
  const DAY_HEADER_H = 8;
  const TIME_W  = 18;
  const ACT_W   = 54;
  const LOC_X   = ML + TIME_W + ACT_W;
  const LOC_W   = CW - TIME_W - ACT_W;
  const LINE    = 3.7;   // line height for main text (8/8.5pt)
  const ADDR_LN = 3.3;   // line height for address (7pt)
  const PAD_T   = 2.6;   // top padding inside a row
  const PAD_B   = 2.4;   // bottom padding inside a row

  function drawDayHeader(dayIdx, cont) {
    setFill(doc, NAVY);
    doc.rect(ML, y, CW, DAY_HEADER_H, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    setColor(doc, [160, 200, 255]);
    doc.text(`DAY ${dayIdx + 1}`, ML + 3, y + 5.5);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    setColor(doc, WHITE);
    doc.text(dayLabel(dep, dayIdx) + (cont ? ' (cont.)' : ''), ML + 22, y + 5.5);

    y += DAY_HEADER_H;
  }

  for (let dayIdx = 0; dayIdx < nDays; dayIdx++) {
    const dayItems = items
      .filter((i) => (i.day || 1) - 1 === dayIdx)
      .sort((a, b) => (a.time || '').localeCompare(b.time || ''));

    // Keep the day header with at least its first row on the same page.
    if (y + DAY_HEADER_H + 12 > PAGE_H - 16) {
      doc.addPage();
      y = 14;
    }
    drawDayHeader(dayIdx, false);

    if (dayItems.length === 0) {
      setFill(doc, [249, 250, 251]);
      doc.rect(ML, y, CW, 8, 'F');
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(8.5);
      setColor(doc, GRAY);
      doc.text('No activities scheduled.', ML + 3, y + 5.2);
      y += 8 + 6;
      continue;
    }

    for (let i = 0; i < dayItems.length; i++) {
      const item = dayItems[i];
      // Prepare wrapped text blocks (set font/size before measuring).
      const matchLines = matches ? activityMatchLines(item, matches.fixturesById, matches.teamsById) : [];
      const actText = (displayType(item.activityType) || item.title || '—')
        + (matchLines.length ? ` (${matchLines.length} ${matchLines.length === 1 ? 'match' : 'matches'})` : '');

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      const actLines = doc.splitTextToSize(actText, ACT_W - 3);

      // Each location block: name (blue), address (small gray), map link.
      const locBlocks = getLocations(item).map((loc) => {
        doc.setFontSize(8.5);
        const nameLines = doc.splitTextToSize(loc.name, LOC_W - 3);
        doc.setFontSize(7);
        const addrLines = loc.address ? doc.splitTextToSize(loc.address, LOC_W - 3) : [];
        return { nameLines, addrLines, link: loc.link };
      });
      const BLOCK_GAP = 1.5;
      const blockH = (b) => b.nameLines.length * LINE + b.addrLines.length * ADDR_LN + (b.link ? ADDR_LN : 0);

      doc.setFontSize(7.5);
      const matchTextLines = matchLines.flatMap((m) => doc.splitTextToSize(`• ${m}`, LOC_W - 3));

      const actBlockH = actLines.length * LINE;
      const locsH     = locBlocks.reduce((h, b, k) => h + blockH(b) + (k ? BLOCK_GAP : 0), 0);
      const locBlockH = locsH + matchTextLines.length * ADDR_LN;
      const contentH  = Math.max(LINE, actBlockH, locBlockH);
      const rowH      = PAD_T + contentH + PAD_B;

      // Page break before the row if it won't fit; repeat the day header.
      if (y + rowH > PAGE_H - 16) {
        doc.addPage();
        y = 14;
        drawDayHeader(dayIdx, true);
      }

      const rowY = y;
      const bg   = i % 2 === 0 ? [249, 250, 251] : WHITE;
      setFill(doc, bg);
      doc.rect(ML, rowY, CW, rowH, 'F');

      const baseY = rowY + PAD_T + 2.6; // baseline of the first line

      // Time
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      setColor(doc, DKGRAY);
      doc.text(item.time || '—', ML + 3, baseY);

      // Activity
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      setColor(doc, DKGRAY);
      actLines.forEach((ln, k) => doc.text(ln, ML + TIME_W + 2, baseY + k * LINE));

      // Locations (name, then address below in smaller gray, then map link)
      let locY = baseY;
      locBlocks.forEach((b, k) => {
        if (k) locY += BLOCK_GAP;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8.5);
        setColor(doc, BLUE);
        b.nameLines.forEach((ln, j) => doc.text(ln, LOC_X + 2, locY + j * LINE));
        locY += b.nameLines.length * LINE;

        if (b.addrLines.length) {
          doc.setFontSize(7);
          setColor(doc, GRAY);
          b.addrLines.forEach((ln, j) => doc.text(ln, LOC_X + 2, locY + j * ADDR_LN));
          locY += b.addrLines.length * ADDR_LN;
        }

        // Clickable Google Maps link
        if (b.link) {
          doc.setFontSize(7);
          setColor(doc, BLUE);
          doc.textWithLink('View on map', LOC_X + 2, locY, { url: b.link });
          locY += ADDR_LN;
        }
      });

      // Matches (who plays who, when, where) under a Fixtures activity
      if (matchTextLines.length) {
        const matchTop = locY;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7.5);
        setColor(doc, DKGRAY);
        matchTextLines.forEach((ln, k) => doc.text(ln, LOC_X + 2, matchTop + k * ADDR_LN));
      }

      y += rowH;
    }

    // Bottom border for the day section
    setDraw(doc, LGRAY);
    doc.setLineWidth(0.3);
    doc.line(ML, y, ML + CW, y);
    y += 6;
  }

  // ── Footer ─────────────────────────────────────────────────────────────────
  const totalPages = doc.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    setFill(doc, LGRAY);
    doc.rect(0, PAGE_H - 10, PAGE_W, 10, 'F');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    setColor(doc, GRAY);
    doc.text('MST Sports Tours — Confidential — For group distribution only', ML, PAGE_H - 3.5);
    doc.text(`Page ${p} of ${totalPages}`, PAGE_W - MR, PAGE_H - 3.5, { align: 'right' });
  }

  const filename = `${[tourLabel || 'itinerary', filterLabel].filter(Boolean).join(' ').replace(/[^a-z0-9]/gi, '_').toLowerCase()}_group_itinerary.pdf`;
  doc.save(filename);
}
